/**
 * /claim/[token] — Claim Your Score Page
 *
 * Landing page for startup founders who received a claim link via email.
 * Shows a score snapshot teaser, handles account creation or login,
 * completes the claim, and presents upgrade/rescore CTAs.
 *
 * 5-step state machine:
 * 1. loading  — validating the token
 * 2. preview  — showing the score teaser (unauthenticated)
 * 3. auth     — login or signup form
 * 4. claiming — executing the claim action
 * 5. success  — celebration + dashboard/upgrade CTAs
 * + error     — any error state
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  Platform,
  Pressable,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Sparkles,
  BarChart3,
  FolderOpen,
  Users,
  RefreshCw,
  Home,
  Crown,
  ChevronRight,
  Shield,
  Zap,
  Star,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, ZoomIn } from 'react-native-reanimated';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { handleError, reportError } from '@/lib/errors';
import { CLAIM_REFUSAL_MESSAGES, isClaimRefusalCode } from '@/lib/company/claim-refusals';
import { useAlert } from '@/components/AlertDialog';
import { validateClaimToken, claimSubmission } from '@/lib/supabase';
import type { ValidateClaimResponse, ClaimSubmissionResponse } from '@/lib/supabase';
import { PublicLayout } from '@/components/PublicLayout';
import { ClaimScoreCard } from '@/components/claim/ClaimScoreCard';
import { ClaimAuthFlow } from '@/components/claim/ClaimAuthFlow';
import { getScoreColor } from '@/lib/colors';
import { openPlans } from '@/lib/commerce/navigation';
import { COPY } from '@/lib/commerce/copy';

// ============================================================================
// Types
// ============================================================================

type ClaimStep = 'loading' | 'preview' | 'auth' | 'claiming' | 'success' | 'error';

type ErrorType =
  | 'invalid_token'
  | 'expired_token'
  | 'already_claimed_other'
  | 'email_mismatch'
  | 'network'
  | 'unknown';

// ============================================================================
// Helpers
// ============================================================================

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Highly Investable';
  if (score >= 60) return 'Investment Ready';
  if (score >= 40) return 'Developing';
  return 'Early Stage';
}

// ============================================================================
// Main Component
// ============================================================================

export default function ClaimPage() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const colors = usePublicThemeColors();
  const { error: showError } = useAlert();

  // State machine
  const [step, setStep] = useState<ClaimStep>('loading');
  const [errorType, setErrorType] = useState<ErrorType>('unknown');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Data from token validation
  const [submissionData, setSubmissionData] = useState<ValidateClaimResponse['submission'] | null>(null);

  // Data from claim response
  const [claimResult, setClaimResult] = useState<ClaimSubmissionResponse['claimedSubmission'] | null>(null);

  // ============================================================================
  // Step 1: Token Validation (on mount)
  // ============================================================================

  useEffect(() => {
    if (!token) {
      setErrorType('invalid_token');
      setErrorMessage('No claim token provided.');
      setStep('error');
      return;
    }

    validateToken();
  }, [token]);

  // Set web page title
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.title = submissionData
        ? `Claim ${submissionData.companyName} Score | Inverio`
        : 'Claim Your Score | Inverio';
    }
  }, [submissionData]);

  const validateToken = async () => {
    setStep('loading');
    try {
      const { data, error } = await validateClaimToken(token as string);

      if (error || !data) {
        const errMsg = error || 'Token validation failed';
        if (errMsg.toLowerCase().includes('expired')) {
          setErrorType('expired_token');
        } else {
          setErrorType('invalid_token');
        }
        setErrorMessage(errMsg);
        setStep('error');
        return;
      }

      if (!data.valid) {
        setErrorType('invalid_token');
        setErrorMessage('This claim link is no longer valid.');
        setStep('error');
        return;
      }

      setSubmissionData(data.submission);

      // If already claimed, show special message
      if (data.submission.alreadyClaimed) {
        setStep('preview'); // Let the preview handle the "already claimed" state
      } else {
        setStep('preview');
      }
    } catch (err) {
      reportError(err, { context: 'ClaimPage.validateToken' });
      setErrorType('network');
      setErrorMessage('Network error. Please check your connection and try again.');
      setStep('error');
    }
  };

  // ============================================================================
  // Step 3 -> 4: After auth, perform the claim
  // ============================================================================

  const handleAuthComplete = async () => {
    setStep('claiming');

    try {
      const { data, error, code } = await claimSubmission(token as string);

      if (error || !data) {
        const errMsg = error || 'Claim failed';
        if (isClaimRefusalCode(code)) {
          // 119-03: a closed company refuses the claim (409 company_closed /
          // erasure_hold); branch on the code, not the message.
          setErrorType('unknown');
          setErrorMessage(CLAIM_REFUSAL_MESSAGES[code]);
        } else if (errMsg.toLowerCase().includes('email') && errMsg.toLowerCase().includes('match')) {
          setErrorType('email_mismatch');
          setErrorMessage('The email you used does not match the submission contact email.');
        } else if (errMsg.toLowerCase().includes('already claimed')) {
          setErrorType('already_claimed_other');
          setErrorMessage('This score has already been claimed by another account.');
        } else {
          setErrorType('unknown');
          setErrorMessage(errMsg);
        }
        setStep('error');
        return;
      }

      // If the submission was already claimed by this user, treat as success
      if (data.alreadyClaimed) {
        setClaimResult(data.claimedSubmission);
        setStep('success');
        return;
      }

      setClaimResult(data.claimedSubmission);
      setStep('success');
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'ClaimPage.handleClaim' });
      if (!isCancelled) {
        setErrorType('network');
        setErrorMessage(message);
        setStep('error');
      }
    }
  };

  // ============================================================================
  // Navigation Handlers
  // ============================================================================

  const handleGoToDashboard = () => {
    router.replace('/(startup)/startup-dashboard');
  };

  const handleUpgrade = openPlans ? () => openPlans!(router, 'startup') : null;

  const handleGoHome = () => {
    router.replace('/');
  };

  const handleRetry = () => {
    validateToken();
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <PublicLayout title="Claim Your Score" maxWidth={600}>
      {/* Step: Loading */}
      {step === 'loading' && <LoadingState colors={colors} />}

      {/* Step: Preview */}
      {step === 'preview' && submissionData && (
        <PreviewState
          submissionData={submissionData}
          colors={colors}
          onClaim={() => setStep('auth')}
          onGoToDashboard={handleGoToDashboard}
        />
      )}

      {/* Step: Auth */}
      {step === 'auth' && submissionData && (
        <ClaimAuthFlow
          contactEmail={submissionData.contactEmail}
          existingAccount={submissionData.existingAccount}
          onAuthenticated={handleAuthComplete}
          onBack={() => setStep('preview')}
        />
      )}

      {/* Step: Claiming */}
      {step === 'claiming' && <ClaimingState colors={colors} />}

      {/* Step: Success */}
      {step === 'success' && claimResult && (
        <SuccessState
          claimResult={claimResult}
          colors={colors}
          onGoToDashboard={handleGoToDashboard}
          onUpgrade={handleUpgrade}
        />
      )}

      {/* Step: Error */}
      {step === 'error' && (
        <ErrorState
          errorType={errorType}
          errorMessage={errorMessage}
          colors={colors}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
        />
      )}
    </PublicLayout>
  );
}

// ============================================================================
// Loading State
// ============================================================================

function LoadingState({ colors }: { colors: ReturnType<typeof usePublicThemeColors> }) {
  return (
    <Animated.View
      entering={FadeInUp.delay(100).duration(500)}
      style={{ alignItems: 'center', paddingVertical: 60 }}
    >
      <ActivityIndicator size="large" color={colors.primary[500]} />
      <Text
        className="text-base font-medium"
        style={{
          color: colors.text.secondary,
          marginTop: 16,
        }}
      >
        Validating your link...
      </Text>
    </Animated.View>
  );
}

// ============================================================================
// Preview State
// ============================================================================

function PreviewState({
  submissionData,
  colors,
  onClaim,
  onGoToDashboard,
}: {
  submissionData: ValidateClaimResponse['submission'];
  colors: ReturnType<typeof usePublicThemeColors>;
  onClaim: () => void;
  onGoToDashboard: () => void;
}) {
  const alreadyClaimed = submissionData.alreadyClaimed;

  return (
    <View>
      {/* Score Card */}
      <ClaimScoreCard
        companyName={submissionData.companyName}
        overallScore={submissionData.scoreSnapshot.overallScore}
        confidence={submissionData.scoreSnapshot.confidence}
        topRiskFlag={submissionData.scoreSnapshot.topRiskFlag}
        investorName={submissionData.investorName}
        showUpgradeTeaser={!alreadyClaimed}
      />

      {/* Explanation */}
      <Animated.View
        entering={FadeInUp.delay(300).duration(500)}
        style={{ marginTop: 24, paddingHorizontal: 4 }}
      >
        <Text
          className="text-sm"
          style={{
            color: colors.text.secondary,
            lineHeight: 22,
            textAlign: 'center',
          }}
        >
          Your deck was scored by Inverio&apos;s AI when {submissionData.investorName} received it.
          Claim your page to access the full report and manage your investment profile.
        </Text>
      </Animated.View>

      {/* CTA Button */}
      <Animated.View entering={FadeInUp.delay(400).duration(500)} style={{ marginTop: 24 }}>
        {alreadyClaimed ? (
          <AnimatedPressable
            onPress={onGoToDashboard}
            style={{
              borderRadius: 16,
              paddingVertical: 18,
              backgroundColor: colors.primary[600],
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
            }}
            accessibilityRole="button"
            accessibilityLabel="Go to dashboard"
          >
            <Home size={20} color={colors.text.inverse} />
            <Text
              className="text-lg font-semibold"
              style={{ color: colors.text.inverse }}
            >
              Already Claimed — Go to Dashboard
            </Text>
          </AnimatedPressable>
        ) : (
          <AnimatedPressable
            onPress={onClaim}
            style={{
              borderRadius: 16,
              paddingVertical: 18,
              backgroundColor: colors.primary[600],
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              shadowColor: colors.primary[500],
              shadowOpacity: 0.3,
              shadowRadius: 12,
              shadowOffset: { width: 0, height: 6 },
              elevation: 6,
            }}
            accessibilityRole="button"
            accessibilityLabel="Claim your page"
          >
            <Sparkles size={20} color={colors.text.inverse} />
            <Text
              className="text-lg font-bold"
              style={{ color: colors.text.inverse }}
            >
              Claim Your Page
            </Text>
            <ArrowRight size={20} color={colors.text.inverse} />
          </AnimatedPressable>
        )}
      </Animated.View>

      {/* Free vs Upgrade Feature Lists */}
      {!alreadyClaimed && (
        <Animated.View entering={FadeInUp.delay(500).duration(500)} style={{ marginTop: 28 }}>
          {/* What you get for free */}
          <View
            style={{
              backgroundColor: colors.surface.primary,
              borderRadius: 20,
              padding: 20,
              marginBottom: 14,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Shield size={18} color={colors.success} />
              <Text
                className="text-base font-semibold"
                style={{ color: colors.text.primary }}
              >
                What you get for free
              </Text>
            </View>
            <FeatureBullet text="Overall investment readiness score" color={colors.success} colors={colors} />
            <FeatureBullet text="Confidence level indicator" color={colors.success} colors={colors} />
            <FeatureBullet text="Top risk flag identification" color={colors.success} colors={colors} />
          </View>

          {/* Upgrade to unlock */}
          {COPY.claimLocked && (
            <View
              style={{
                backgroundColor: colors.primary[50],
                borderRadius: 20,
                padding: 20,
                borderWidth: 1,
                borderColor: colors.primary[200],
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Crown size={18} color={colors.primary[600]} />
                <Text
                  className="text-base font-semibold"
                  style={{ color: colors.primary[700] }}
                >
                  {COPY.claimLocked.heading}
                </Text>
              </View>
              <FeatureBullet text="Full 5-category score breakdown" color={colors.primary[500]} colors={colors} />
              <FeatureBullet text="Detailed AI recommendations" color={colors.primary[500]} colors={colors} />
              <FeatureBullet text="Rescore capability" color={colors.primary[500]} colors={colors} />
              <FeatureBullet text="Secure data room" color={colors.primary[500]} colors={colors} />
              <FeatureBullet text="Marketplace visibility to investors" color={colors.primary[500]} colors={colors} />
            </View>
          )}
        </Animated.View>
      )}
    </View>
  );
}

// ============================================================================
// Feature Bullet Sub-component
// ============================================================================

function FeatureBullet({
  text,
  color,
  colors,
}: {
  text: string;
  color: string;
  colors: ReturnType<typeof usePublicThemeColors>;
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <CheckCircle2 size={16} color={color} />
      <Text
        className="text-sm"
        style={{
          color: colors.text.secondary,
          flex: 1,
        }}
      >
        {text}
      </Text>
    </View>
  );
}

// ============================================================================
// Claiming State
// ============================================================================

function ClaimingState({ colors }: { colors: ReturnType<typeof usePublicThemeColors> }) {
  return (
    <Animated.View
      entering={FadeInUp.delay(100).duration(500)}
      style={{ alignItems: 'center', paddingVertical: 60 }}
    >
      <ActivityIndicator size="large" color={colors.primary[500]} />
      <Text
        className="text-lg font-semibold"
        style={{
          color: colors.text.primary,
          marginTop: 20,
        }}
      >
        Claiming your score...
      </Text>
      <Text
        className="text-sm"
        style={{
          color: colors.text.secondary,
          marginTop: 6,
        }}
      >
        Setting up your profile
      </Text>
    </Animated.View>
  );
}

// ============================================================================
// Success State
// ============================================================================

function SuccessState({
  claimResult,
  colors,
  onGoToDashboard,
  onUpgrade,
}: {
  claimResult: ClaimSubmissionResponse['claimedSubmission'];
  colors: ReturnType<typeof usePublicThemeColors>;
  onGoToDashboard: () => void;
  onUpgrade: (() => void) | null;
}) {
  const scoreColor = getScoreColor(claimResult.scoreSnapshot.overallScore);

  return (
    <View>
      {/* Celebration Header */}
      <Animated.View
        entering={ZoomIn.delay(100).duration(600)}
        style={{ alignItems: 'center', marginBottom: 28 }}
      >
        <View
          style={{
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: `${colors.success}15`,
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <CheckCircle2 size={44} color={colors.success} />
        </View>
        <Text
          className="text-2xl font-bold"
          style={{
            color: colors.text.primary,
            textAlign: 'center',
          }}
        >
          Your score page is ready!
        </Text>
        <Text
          className="text-sm"
          style={{
            color: colors.text.secondary,
            textAlign: 'center',
            marginTop: 6,
            lineHeight: 22,
          }}
        >
          {claimResult.companyName} has been added to your Inverio dashboard.
        </Text>
      </Animated.View>

      {/* Score Summary Card */}
      <Animated.View
        entering={FadeInUp.delay(300).duration(500)}
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 24,
          padding: 24,
          marginBottom: 20,
          shadowColor: '#000',
          shadowOpacity: 0.06,
          shadowRadius: 16,
          shadowOffset: { width: 0, height: 4 },
          elevation: 4,
        }}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <View style={{ flex: 1 }}>
            <Text
              className="text-lg font-bold"
              style={{
                color: colors.text.primary,
                marginBottom: 4,
              }}
            >
              {claimResult.companyName}
            </Text>
            <View
              style={{
                alignSelf: 'flex-start',
                paddingHorizontal: 12,
                paddingVertical: 5,
                borderRadius: 12,
                backgroundColor: `${scoreColor}15`,
              }}
            >
              <Text
                className="text-sm font-semibold"
                style={{ color: scoreColor }}
              >
                {getScoreLabel(claimResult.scoreSnapshot.overallScore)}
              </Text>
            </View>
          </View>
          <View style={{ alignItems: 'center' }}>
            <Text
              className="text-5xl font-bold"
              style={{
                color: scoreColor,
                letterSpacing: -2,
              }}
            >
              {claimResult.scoreSnapshot.overallScore}
            </Text>
            <Text
              className="text-xs font-medium"
              style={{
                color: colors.text.tertiary,
                marginTop: -4,
              }}
            >
              SCORE
            </Text>
          </View>
        </View>

        {/* Quick Stats */}
        <View
          style={{
            flexDirection: 'row',
            gap: 12,
            marginTop: 16,
            paddingTop: 16,
            borderTopWidth: 1,
            borderTopColor: colors.border.light,
          }}
        >
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text
              className="text-xl font-bold"
              style={{ color: colors.text.primary }}
            >
              {claimResult.scoreSnapshot.confidence}%
            </Text>
            <Text
              className="text-xs font-medium"
              style={{ color: colors.text.tertiary }}
            >
              Confidence
            </Text>
          </View>
          <View
            style={{
              width: 1,
              backgroundColor: colors.border.light,
            }}
          />
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text
              className="text-sm font-semibold"
              style={{ color: claimResult.scoreSnapshot.topRiskFlag ? colors.warning : colors.success }}
            >
              {claimResult.scoreSnapshot.topRiskFlag || 'None'}
            </Text>
            <Text
              className="text-xs font-medium"
              style={{ color: colors.text.tertiary }}
            >
              Top Risk
            </Text>
          </View>
        </View>
      </Animated.View>

      {/* CTA Buttons */}
      <Animated.View entering={FadeInUp.delay(450).duration(500)}>
        {/* Primary: Go to Dashboard */}
        <AnimatedPressable
          onPress={onGoToDashboard}
          style={{
            borderRadius: 16,
            paddingVertical: 18,
            backgroundColor: colors.primary[600],
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            marginBottom: 12,
            shadowColor: colors.primary[500],
            shadowOpacity: 0.3,
            shadowRadius: 12,
            shadowOffset: { width: 0, height: 6 },
            elevation: 6,
          }}
          accessibilityRole="button"
          accessibilityLabel="Go to dashboard"
        >
          <Home size={20} color={colors.text.inverse} />
          <Text
            className="text-lg font-bold"
            style={{ color: colors.text.inverse }}
          >
            Go to Dashboard
          </Text>
          <ArrowRight size={20} color={colors.text.inverse} />
        </AnimatedPressable>

        {/* Secondary: see the startup plans (COPY.claimLocked.cta) */}
        {onUpgrade && COPY.claimLocked && (
          <AnimatedPressable
            onPress={onUpgrade}
            style={{
              borderRadius: 16,
              paddingVertical: 16,
              borderWidth: 2,
              borderColor: colors.primary[200],
              backgroundColor: colors.primary[50],
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
            }}
            accessibilityRole="button"
            accessibilityLabel="See plans"
          >
            <Star size={20} color={colors.primary[600]} />
            <Text
              className="text-base font-semibold"
              style={{ color: colors.primary[700] }}
            >
              {COPY.claimLocked.cta}
            </Text>
            <ChevronRight size={18} color={colors.primary[600]} />
          </AnimatedPressable>
        )}
      </Animated.View>
    </View>
  );
}

// ============================================================================
// Error State
// ============================================================================

function ErrorState({
  errorType,
  errorMessage,
  colors,
  onRetry,
  onGoHome,
}: {
  errorType: ErrorType;
  errorMessage: string;
  colors: ReturnType<typeof usePublicThemeColors>;
  onRetry: () => void;
  onGoHome: () => void;
}) {
  const getErrorTitle = (): string => {
    switch (errorType) {
      case 'invalid_token':
        return 'Invalid Link';
      case 'expired_token':
        return 'Link Expired';
      case 'already_claimed_other':
        return 'Already Claimed';
      case 'email_mismatch':
        return 'Email Mismatch';
      case 'network':
        return 'Connection Error';
      default:
        return 'Something Went Wrong';
    }
  };

  const getErrorDescription = (): string => {
    switch (errorType) {
      case 'invalid_token':
        return 'This claim link is no longer valid. It may have been used already or the URL is incorrect.';
      case 'expired_token':
        return 'This claim link has expired. Please contact the investor who shared your score to request a new link.';
      case 'already_claimed_other':
        return 'This score has already been claimed by another account. If you believe this is an error, please contact support.';
      case 'email_mismatch':
        return 'The email you signed up with does not match the email used in the submission. Please use the correct email address.';
      case 'network':
        return 'We could not connect to the server. Please check your internet connection and try again.';
      default:
        return errorMessage || 'An unexpected error occurred. Please try again.';
    }
  };

  const isRetryable = errorType === 'network' || errorType === 'unknown';

  return (
    <Animated.View
      entering={FadeInUp.delay(100).duration(500)}
      style={{ alignItems: 'center', paddingVertical: 40 }}
    >
      <View
        style={{
          width: 72,
          height: 72,
          borderRadius: 36,
          backgroundColor: `${colors.error}15`,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <AlertCircle size={36} color={colors.error} />
      </View>

      <Text
        className="text-2xl font-bold"
        style={{
          color: colors.text.primary,
          textAlign: 'center',
          marginBottom: 8,
        }}
      >
        {getErrorTitle()}
      </Text>

      <Text
        className="text-base"
        style={{
          color: colors.text.secondary,
          textAlign: 'center',
          lineHeight: 22,
          paddingHorizontal: 8,
          marginBottom: 28,
        }}
      >
        {getErrorDescription()}
      </Text>

      {/* CTA Buttons */}
      <View style={{ width: '100%', gap: 10 }}>
        {isRetryable && (
          <AnimatedPressable
            onPress={onRetry}
            style={{
              borderRadius: 14,
              paddingVertical: 16,
              backgroundColor: colors.primary[600],
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
            }}
            accessibilityRole="button"
            accessibilityLabel="Try again"
          >
            <RefreshCw size={18} color={colors.text.inverse} />
            <Text
              className="text-base font-semibold"
              style={{ color: colors.text.inverse }}
            >
              Try Again
            </Text>
          </AnimatedPressable>
        )}

        <AnimatedPressable
          onPress={onGoHome}
          style={{
            borderRadius: 14,
            paddingVertical: 16,
            borderWidth: 1,
            borderColor: colors.border.medium,
            backgroundColor: colors.surface.primary,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
          }}
          accessibilityRole="button"
          accessibilityLabel="Go to home page"
        >
          <Home size={18} color={colors.text.secondary} />
          <Text
            className="text-base font-semibold"
            style={{ color: colors.text.secondary }}
          >
            Go to Home
          </Text>
        </AnimatedPressable>

        {/* Contact support for already-claimed errors */}
        {errorType === 'already_claimed_other' && (
          <Pressable
            style={{
              paddingVertical: 12,
              alignItems: 'center',
            }}
            accessibilityRole="link"
            accessibilityLabel="Contact support"
          >
            <Text
              className="text-sm font-medium"
              style={{ color: colors.primary[600] }}
            >
              Contact Support: support@inverio.ai
            </Text>
          </Pressable>
        )}
      </View>
    </Animated.View>
  );
}
